// exp_PORTFOLIO-58018_file
import { DefaultRootState } from 'react-redux';

import { Nullable } from 'types';
import { PRESETS_CONFIG, VrCampaignErrors, VrPresets } from 'utils/vrCampaign';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';

export interface VrCampaignVacancy {
    vacancyId: Nullable<number>;
    vacancyName: Nullable<string>;
    vacancyUrl: Nullable<string>;
    areaId: Nullable<number>;
}

// ----------------------- actions -------------------------------------
const SET_VR_CAMPAIGN_VACANCY = 'SET_VR_CAMPAIGN_VACANCY';
const SET_VR_CAMPAIGN_PHONE = 'SET_VR_CAMPAIGN_PHONE';
const SET_VR_CAMPAIGN_BUDGET = 'SET_VR_CAMPAIGN_BUDGET';
const SET_VR_CAMPAIGN_RESPONSES = 'SET_VR_CAMPAIGN_RESPONSES';
const SET_VR_CAMPAIGN_REQUIREMENTS = 'SET_VR_CAMPAIGN_REQUIREMENTS';
const SET_VR_CAMPAIGN_AREAS = 'SET_VR_CAMPAIGN_AREAS';
const SET_VR_CAMPAIGN_PRESET = 'SET_VR_CAMPAIGN_PRESET';
const SET_VR_CAMPAIGN_ERRORS = 'SET_VR_CAMPAIGN_ERRORS';
const RESET_VR_CAMPAIGN = 'RESET_VR_CAMPAIGN';
const SET_VR_CAMPAIGN_REQUEST_MODAL_VISIBLE = 'SET_VR_CAMPAIGN_REQUEST_MODAL_VISIBLE';

interface PayloadTypes {
    [SET_VR_CAMPAIGN_VACANCY]: VrCampaignVacancy;
    [SET_VR_CAMPAIGN_PHONE]: string;
    [SET_VR_CAMPAIGN_BUDGET]: number;
    [SET_VR_CAMPAIGN_RESPONSES]: number;
    [SET_VR_CAMPAIGN_REQUIREMENTS]: string;
    [SET_VR_CAMPAIGN_AREAS]: number[];
    [SET_VR_CAMPAIGN_PRESET]: VrPresets;
    [SET_VR_CAMPAIGN_ERRORS]: Partial<VrCampaignErrors>;
    [RESET_VR_CAMPAIGN]: null;
    [SET_VR_CAMPAIGN_REQUEST_MODAL_VISIBLE]: boolean;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setVrCampaignVacancy = ActionCreator(SET_VR_CAMPAIGN_VACANCY);
export const setVrCampaignPhone = ActionCreator(SET_VR_CAMPAIGN_PHONE);
export const setVrCampaignBudget = ActionCreator(SET_VR_CAMPAIGN_BUDGET);
export const setVrCampaignResponses = ActionCreator(SET_VR_CAMPAIGN_RESPONSES);
export const setVrCampaignRequirements = ActionCreator(SET_VR_CAMPAIGN_REQUIREMENTS);
export const setVrCampaignAreas = ActionCreator(SET_VR_CAMPAIGN_AREAS);
export const setVrCampaignPreset = ActionCreator(SET_VR_CAMPAIGN_PRESET);
export const setVrCampaignErrors = ActionCreator(SET_VR_CAMPAIGN_ERRORS);
export const resetVrCampaign = ActionCreator(RESET_VR_CAMPAIGN);
export const setVrCampaignRequestModalVisible = ActionCreator(SET_VR_CAMPAIGN_REQUEST_MODAL_VISIBLE);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
export interface VrCampaignState {
    vacancy: VrCampaignVacancy;
    phone: Nullable<string>;
    budget: number;
    responses: number;
    requirements: string;
    areas: number[];
    preset: VrPresets;
    errors: VrCampaignErrors;
    requestModalVisible: boolean;
}

export const VR_CAMPAIGN_INITIAL_ERRORS: VrCampaignErrors = {
    responses: false,
    budget: false,
    areas: false,
    requirements: false,
};

export const VR_CAMPAIGN_INITIAL_STATE: VrCampaignState = {
    vacancy: {
        vacancyId: null,
        vacancyName: null,
        vacancyUrl: null,
        areaId: null,
    },
    phone: null,
    budget: PRESETS_CONFIG[VrPresets.Medium].budget,
    responses: PRESETS_CONFIG[VrPresets.Medium].responses,
    requirements: '',
    areas: [],
    preset: VrPresets.Medium,
    errors: VR_CAMPAIGN_INITIAL_ERRORS,
    requestModalVisible: false,
};

export default createReducer<VrCampaignState, PayloadTypes>(VR_CAMPAIGN_INITIAL_STATE, {
    [SET_VR_CAMPAIGN_VACANCY]: (state, { payload }) => ({
        ...state,
        vacancy: payload,
    }),
    [SET_VR_CAMPAIGN_PHONE]: (state, { payload }) => ({
        ...state,
        phone: payload,
    }),
    [SET_VR_CAMPAIGN_BUDGET]: (state, { payload }) => ({
        ...state,
        budget: payload,
    }),
    [SET_VR_CAMPAIGN_RESPONSES]: (state, { payload }) => ({
        ...state,
        responses: payload,
    }),
    [SET_VR_CAMPAIGN_REQUIREMENTS]: (state, { payload }) => ({
        ...state,
        requirements: payload,
    }),
    [SET_VR_CAMPAIGN_AREAS]: (state, { payload }) => ({
        ...state,
        areas: payload,
    }),
    [SET_VR_CAMPAIGN_PRESET]: (state, { payload }) => ({
        ...state,
        preset: payload,
    }),
    [SET_VR_CAMPAIGN_ERRORS]: (state, { payload }) => ({
        ...state,
        errors: { ...state.errors, ...payload },
    }),
    [SET_VR_CAMPAIGN_REQUEST_MODAL_VISIBLE]: (state, { payload }) => ({
        ...state,
        requestModalVisible: payload,
    }),
    [RESET_VR_CAMPAIGN]: () => ({
        ...VR_CAMPAIGN_INITIAL_STATE,
    }),
});
// ----------------------- /reducer --------------------------------------

// ----------------------- selectors ------------------------------------
export const selectVrCampaignVacancy = (state: DefaultRootState): VrCampaignVacancy => state.vrCampaign.vacancy;
export const selectVrCampaignVacancyId = (state: DefaultRootState): Nullable<number> =>
    state.vrCampaign.vacancy.vacancyId;
export const selectVrCampaignVacancyName = (state: DefaultRootState): Nullable<string> =>
    state.vrCampaign.vacancy.vacancyName;
export const selectVrCampaignPhone = (state: DefaultRootState): Nullable<string> => state.vrCampaign.phone;
export const selectVrCampaignBudget = (state: DefaultRootState): number => state.vrCampaign.budget;
export const selectVrCampaignResponses = (state: DefaultRootState): number => state.vrCampaign.responses;
export const selectVrCampaignRequirements = (state: DefaultRootState): string => state.vrCampaign.requirements;
export const selectVrCampaignAreas = (state: DefaultRootState): number[] => state.vrCampaign.areas;
export const selectVrCampaignPreset = (state: DefaultRootState): VrPresets => state.vrCampaign.preset;
export const selectVrCampaignErrors = (state: DefaultRootState): VrCampaignErrors => state.vrCampaign.errors;
export const selectVrCampaignRequestModalVisible = (state: DefaultRootState): boolean =>
    state.vrCampaign.requestModalVisible;
// ----------------------- /selectors -----------------------------------
